/**
 * Reset Password Screen
 * HIGH Priority: Complete password reset flow
 */

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/modules/auth.api";
import { FloatingActionButtons } from "../components/ui/FloatingActionButtons";
import { ROUTES } from "../constants/routes";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token"), [searchParams]);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError("");
  };

  const validatePassword = () => {
    if (formData.password.length < 8) {
      setError(t("auth.resetPassword.passwordTooShort"));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("auth.resetPassword.passwordMismatch"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t("auth.resetPassword.invalidToken"));
      return;
    }

    if (!validatePassword()) return;

    setLoading(true);
    setError("");

    try {
      const response = await resetPassword({
        token,
        password: formData.password,
      });

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 2500);
        return;
      }

      setError(response.message || t("auth.resetPassword.resetFailed"));
    } catch (err: any) {
      setError(err?.message || t("auth.resetPassword.resetFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
        <div className="max-w-md w-full card-professional rounded-2xl p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full">
            <FaLock className="text-red-600 text-3xl" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-[var(--text-primary)] text-center">
            {t("auth.resetPassword.invalidToken")}
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)] text-center">
            {t("auth.resetPassword.invalidToken")}
          </p>
          <Link
            to={ROUTES.LOGIN}
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            <FaArrowLeft />
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
        <div className="max-w-md w-full card-professional rounded-2xl p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
            <FaCheckCircle className="text-green-600 text-3xl" />
          </div>

          <h2 className="mt-6 text-2xl font-bold text-[var(--text-primary)] text-center">
            {t("auth.resetPassword.passwordResetSuccess")}
          </h2>

          <p className="mt-3 text-sm text-[var(--text-secondary)] text-center">
            {t("auth.resetPassword.redirectingToLogin")}
          </p>

          <Link
            to={ROUTES.LOGIN}
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            <FaArrowLeft />
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
      <div className="max-w-md w-full card-professional rounded-2xl p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full">
          <FaLock className="text-primary text-3xl" />
        </div>

        <h2 className="mt-6 text-2xl font-bold text-[var(--text-primary)] text-center">
          {t("auth.resetPassword.title")}
        </h2>

        <p className="mt-2 text-sm text-[var(--text-secondary)] text-center">
          {t("auth.resetPassword.subtitle")}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              {t("auth.resetPassword.newPassword")}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder={t("auth.resetPassword.newPasswordPlaceholder")}
                className="w-full px-4 py-3 pr-12 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                aria-label={
                  showPassword
                    ? t("common.collapse", "Hide")
                    : t("common.expand", "Show")
                }
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              {t("auth.resetPassword.passwordTooShort")}
            </p>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              {t("auth.resetPassword.confirmPassword")}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength={8}
                placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
                className="w-full px-4 py-3 pr-12 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                aria-label={
                  showConfirmPassword
                    ? t("common.collapse", "Hide")
                    : t("common.expand", "Show")
                }
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loading
              ? t("auth.resetPassword.resetting")
              : t("auth.resetPassword.resetPassword")}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={ROUTES.LOGIN}
            className="text-sm text-primary hover:text-pink-700 font-medium"
          >
            <FaArrowLeft className="inline mr-2" />
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default ResetPassword;
